import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import BedbankSummaryPriceDetails from './BedbankSummaryPriceDetails'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import WalledContent from 'components/Common/WalledContent'
import BedbankPromotionCancellationPolicy from 'components/OfferPage/Bedbank/BedbankPromotionCancellationPolicy'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import { scrollToId } from 'lib/window/scrollUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;

  ${mediaQueryUp.largeDesktop} {
    min-width: ${rem(340)};
  }

  > * + * {
    margin-top: ${rem(8)};
  }
`

const Wrapper = styled(Group)`
  min-width: 0;
`

export const DetailSegment = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  width: 100%;
  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    display: grid;
    grid-template-columns: 60% 1fr;
    min-height: ${rem(160)};
  }
`

const LeftDetailSegment = styled.div`
  align-self: flex-start;
  > * + * {
    margin-top: ${rem(16)}
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};
  ${mediaQueryUp.desktop} {
    margin-left: ${rem(20)};
  }
`
const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const BuyButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

interface Props {
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView
  rooms: Array<App.Occupants>;
  duration: number;
  showNonCancellation?: boolean;
  checkInDate?: moment.Moment;
  checkOutDate?: moment.Moment;
  flightsEnabled: boolean;
  flightPrice?: number;
}

function BedbankSummaryPromotion({ offer, packageView, checkInDate, checkOutDate, showNonCancellation, rooms, duration, flightsEnabled, flightPrice }: Props) {
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)

  const pkg = packageView.package
  const rate = packageView.cheapestRate
  let bundleRate
  if (rate) {
    bundleRate = packageView.bundleRates.find((bundleRate) => bundleRate.id === rate.id)
  }

  const promotion = rate?.id ? packageView.promotions[rate.id] : undefined

  return (
    <Wrapper direction="vertical" gap={8} horizontalAlign="start" fullWidth>
      <MainSection>
        <DetailSegment>
          <LeftDetailSegment>
            <ProductTypeLabel productType="limited_time_special"/>
            <Heading variant="heading5">{promotion.name}</Heading>
            {rate && (
              <BedbankCancellationPolicy rate={rate} offer={offer} showNonCancellation={showNonCancellation} />
            )}
            {!validCheckinCheckoutDates && promotion.cancellationPolicy && (
              <BedbankPromotionCancellationPolicy policyCode={promotion.cancellationPolicy} />
            )}

            <BedbankOfferInclusions
              heading="Special inclusions for select packages only"
              content={promotion.rateInclusionsLong}
              inclusions={promotion.inclusions}
              maxHeight={160}
            />
            <TravelPeriodSection>
              <BodyText variant="small" weight="normal" colour="neutral-two">
                Travel between: {moment(promotion.travelPeriod.from).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(promotion.travelPeriod.to).format(DMY_CASUAL_SHORT_FORMAT)}.
                Minimum stay: {pluralizeToString('night', promotion.los)}
              </BodyText>
            </TravelPeriodSection>
          </LeftDetailSegment>
          <RightDetailSegment>
            {offer.sell && (
              <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
                <BedbankSummaryPriceDetails
                  rate={rate}
                  bundleRate={bundleRate}
                  rooms={rooms}
                  offer={offer}
                  duration={duration}
                  flightPrice={flightPrice}
                  flightsEnabled={flightsEnabled}
                  promotion
                />
              </WalledContent>
            )}

            <BuyButton
              kind="primary"
              data-filter="checkin"
              fit="full-width"
              onClick={() => scrollToId(pkg.roomId)}
            >
              Check availability
            </BuyButton>
          </RightDetailSegment>
        </DetailSegment>
      </MainSection>
    </Wrapper>
  )
}

export default BedbankSummaryPromotion
